import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import CLIENT from '../../store/actions/CLIENT';
import './assets/styles/ContentRatingQualityContainer.scss';

const MIXPANEL_BOARD_URL =
  'https://mixpanel.com/project/3486116/view/3988212/app/boards/?mp_source=slack#id=11143363&filters=%7E%28%7E%28dataGroupId%7E%272048276863072140369%7EresourceType%7E%27user%7EpropertyName%7E%27subscription_type%7EpropertyObjectKey%7Enull%7EpropertyDefaultType%7E%27string%7EpropertyType%7E%27string%7EfilterOperator%7E%27equals%7EfilterValue%7E%28%7E%27paid%29%7ElimitValues%7Efalse%7EdefaultEmpty%7Efalse%7EactiveValue%7E%28%7E%27paid%29%29%29';

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function defaultRange() {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 6);
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
}

function getMixpanelBoardUrl(startDate, endDate) {
  return MIXPANEL_BOARD_URL;
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(2)}%`;
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function shortText(value, fallback = '-') {
  if (!value) return fallback;
  return String(value);
}

function formatSourceLabel(value) {
  if (!value) return '-';
  return String(value)
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getUserLabels(userEmails, userIds) {
  const emails = (userEmails || []).map((email) => String(email)).filter(Boolean);
  if (emails.length) return emails;
  return (userIds || []).map((id) => String(id)).filter(Boolean);
}

function UserCount({ count, userEmails, userIds }) {
  const [position, setPosition] = useState();
  const users = getUserLabels(userEmails, userIds);
  const visibleUsers = users.slice(0, 30);
  const hiddenCount = Math.max(users.length - visibleUsers.length, 0);
  const showPopover = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition({
      left: Math.min(rect.left, window.innerWidth - 440),
      top: rect.bottom + 8,
    });
  };

  return (
    <span
      className="crq-user-count"
      onFocus={showPopover}
      onMouseEnter={showPopover}
      onBlur={() => setPosition(undefined)}
      onMouseLeave={() => setPosition(undefined)}
      tabIndex="0"
    >
      {formatNumber(count)}
      {position ? (
        <span className="crq-user-popover" role="tooltip" style={position}>
          <strong>Users</strong>
          {visibleUsers.length ? (
            <>
              {visibleUsers.map((user) => (
                <span key={user}>{user}</span>
              ))}
              {hiddenCount ? <em>+{hiddenCount} more</em> : null}
            </>
          ) : (
            <span>No user emails available</span>
          )}
        </span>
      ) : null}
    </span>
  );
}

function UserPopover({ users, position }) {
  if (!position) return null;
  const visibleUsers = users.slice(0, 30);
  const hiddenCount = Math.max(users.length - visibleUsers.length, 0);

  return (
    <span className="crq-user-popover" role="tooltip" style={position}>
      <strong>Users</strong>
      {visibleUsers.length ? (
        <>
          {visibleUsers.map((user) => (
            <span key={user}>{user}</span>
          ))}
          {hiddenCount ? <em>+{hiddenCount} more</em> : null}
        </>
      ) : (
        <span>No user emails available</span>
      )}
    </span>
  );
}

function UserCard({ label, value, helper, userEmails, userIds }) {
  const [position, setPosition] = useState();
  const users = getUserLabels(userEmails, userIds);
  const showPopover = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition({
      left: Math.min(rect.left + 24, window.innerWidth - 440),
      top: rect.bottom - 8,
    });
  };

  return (
    <div
      className="crq-card crq-card--hoverable"
      onFocus={showPopover}
      onMouseEnter={showPopover}
      onBlur={() => setPosition(undefined)}
      onMouseLeave={() => setPosition(undefined)}
      tabIndex="0"
    >
      <div className="crq-card__label">{label}</div>
      <div className="crq-card__value">{value}</div>
      {helper ? <div className="crq-card__helper">{helper}</div> : null}
      <UserPopover users={users} position={position} />
    </div>
  );
}

function barOptions(categories, color = '#1868db') {
  return {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, Arial, sans-serif' },
    colors: [color],
    dataLabels: { enabled: false },
    grid: { borderColor: '#edf1f7' },
    plotOptions: { bar: { borderRadius: 3, columnWidth: '54%' } },
    xaxis: {
      categories,
      labels: { rotate: -35, trim: true, style: { fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { formatter: (value) => Math.round(value) } },
    tooltip: { y: { formatter: (value) => formatNumber(value) } },
  };
}

function Card({ label, value, helper }) {
  return (
    <div className="crq-card">
      <div className="crq-card__label">{label}</div>
      <div className="crq-card__value">{value}</div>
      {helper ? <div className="crq-card__helper">{helper}</div> : null}
    </div>
  );
}

function MethodologyNotes({ sourceRecovery }) {
  return (
    <div className="crq-methodology-notes">
      <p>
        <strong>How this differs from Mixpanel:</strong> Mixpanel shows analytics event trends. This dashboard starts from production rating records and adds content details, user emails, and available app-entry context so stakeholders can see what content needs review and how confident the context is.
      </p>
      <p>
        <strong>Accuracy:</strong> rating counts and content details come directly from prod read-only CMS data. User emails are looked up from the prod users collection. Entry source is shown only when a matching rating action is found in activity logs; {formatNumber(sourceRecovery?.recoveredNegativeRatings)} of {formatNumber(sourceRecovery?.totalNegativeRatingsChecked)} one-star ratings have that match.
      </p>
      <p>
        <strong>Not captured:</strong> these are real rating rows. They are included in totals and watchlists, but the missing language or entry-source context should not be over-interpreted.
      </p>
    </div>
  );
}

function SmallTable({ title, rows, nameField }) {
  return (
    <section className="crq-panel">
      <div className="crq-panel__head">
        <h2>{title}</h2>
      </div>
      <div className="crq-table-wrap">
        <table className="crq-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>1-star</th>
              <th>5-star</th>
              <th>Neg. rate</th>
              <th>Risk vs avg</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).slice(0, 8).map((row) => (
              <tr key={shortText(row[nameField])}>
                <td>{shortText(row[nameField])}</td>
                <td>{formatNumber(row.negativeRatings)}</td>
                <td>{formatNumber(row.positiveRatings)}</td>
                <td>{formatPercent(row.negativeRate)}</td>
                <td>{Number(row.liftVsBaseline || 0).toFixed(2)}x</td>
              </tr>
            ))}
            {!rows?.length ? (
              <tr>
                <td colSpan="5">No ratings found for this range.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SourceRecoveryTable({ rows, recovery }) {
  return (
    <section className="crq-panel">
      <div className="crq-panel__head">
        <h2>Rating sources</h2>
        <span>
          {recovery?.enabled
            ? `${formatNumber(recovery.recoveredNegativeRatings)} with source, ${formatNumber(recovery.unrecoveredNegativeRatings)} not captured`
            : 'Source unavailable'}
        </span>
      </div>
      <div className="crq-table-wrap">
        <table className="crq-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>1-star</th>
              <th>Users</th>
              <th>Content items</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).slice(0, 8).map((row) => (
              <tr key={shortText(row.viewed_from)}>
                <td>{formatSourceLabel(row.viewed_from)}</td>
                <td>{formatNumber(row.negativeRatings)}</td>
                <td><UserCount count={row.uniqueUsers} userEmails={row.userEmails} userIds={row.userIds} /></td>
                <td>{formatNumber(row.uniqueContent)}</td>
              </tr>
            ))}
            {!rows?.length ? (
              <tr>
                <td colSpan="4">No source data found for this range.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CoverageGapTables({ missingLanguageRows, missingSourceRows }) {
  return (
    <section className="crq-panel">
      <div className="crq-panel__head">
        <h2>Context not captured</h2>
          <span>
            {formatNumber((missingLanguageRows || []).length)} language rows, {formatNumber((missingSourceRows || []).length)} source rows
          </span>
      </div>
      <div className="crq-two-table-grid">
        <div className="crq-table-wrap">
          <table className="crq-table">
            <thead>
              <tr>
                <th>Missing content language</th>
                <th>1-star</th>
                <th>5-star</th>
                <th>Users</th>
              </tr>
            </thead>
            <tbody>
              {(missingLanguageRows || []).map((row) => (
                <tr key={`missing-language-${row.class_id}`}>
                  <td>{shortText(row.class_name)}</td>
                  <td>{formatNumber(row.negativeRatings)}</td>
                  <td>{formatNumber(row.positiveRatings)}</td>
                  <td><UserCount count={row.uniqueUsers} userEmails={row.userEmails} userIds={row.userIds} /></td>
                </tr>
              ))}
              {!missingLanguageRows?.length ? (
                <tr>
                  <td colSpan="4">No missing content language in this range.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="crq-table-wrap">
          <table className="crq-table">
            <thead>
              <tr>
                <th>Source not captured</th>
                <th>1-star</th>
                <th>Users</th>
              </tr>
            </thead>
            <tbody>
              {(missingSourceRows || []).map((row) => (
                <tr key={`missing-source-${row.class_id}`}>
                  <td>{shortText(row.class_name)}</td>
                  <td>{formatNumber(row.negativeRatings)}</td>
                  <td><UserCount count={row.uniqueUsers} userEmails={row.userEmails} userIds={row.userIds} /></td>
                </tr>
              ))}
              {!missingSourceRows?.length ? (
                <tr>
                  <td colSpan="3">No missing source rows in this range.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ContentRatingQualityContainer() {
  const appState = useSelector((state) => state);
  const initialRange = useMemo(defaultRange, []);
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [minNegativeRatings, setMinNegativeRatings] = useState(3);
  const [dashboard, setDashboard] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await CLIENT({
        url: '/kpi/content-rating-quality',
        state: appState,
        body: {
          startDate,
          endDate,
          minNegativeRatings: Number(minNegativeRatings),
        },
      });
      setDashboard(response.data);
    } catch (requestError) {
      setDashboard(undefined);
      setError(requestError?.response?.data || requestError);
    } finally {
      setLoading(false);
    }
  }, [appState, endDate, minNegativeRatings, startDate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const summary = dashboard?.summary || {};
  const dailyTrend = dashboard?.dailyTrend || [];
  const bySource = dashboard?.bySource || [];
  const byLocale = dashboard?.byLocale || [];
  const byTag = dashboard?.byTag || [];
  const repeatNegativeRaters = dashboard?.repeatNegativeRaters || [];
  const sourceRecovery = dashboard?.sourceRecovery || {};
  const dataCoverage = dashboard?.dataCoverage || {};
  const mixpanelBoardUrl = useMemo(
    () => getMixpanelBoardUrl(startDate, endDate),
    [endDate, startDate]
  );

  const trendOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, Arial, sans-serif' },
    colors: ['#e5484d'],
    dataLabels: { enabled: false },
    grid: { borderColor: '#edf1f7' },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: {
      categories: dailyTrend.map((row) => row.date),
      labels: { rotate: -35, style: { fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { formatter: (value) => `${Number(value).toFixed(0)}%` } },
    tooltip: { y: { formatter: (value) => formatPercent(value) } },
  };

  const sourceChart = {
    options: barOptions(bySource.slice(0, 10).map((row) => formatSourceLabel(row.viewed_from)), '#1868db'),
    series: [{ name: '1-star ratings', data: bySource.slice(0, 10).map((row) => row.negativeRatings) }],
  };
  const localeChart = {
    options: barOptions(byLocale.slice(0, 10).map((row) => row.content_locale), '#2b8a3e'),
    series: [{ name: '1-star ratings', data: byLocale.slice(0, 10).map((row) => row.negativeRatings) }],
  };
  const tagChart = {
    options: barOptions(byTag.slice(0, 10).map((row) => row.tag_name), '#7c3aed'),
    series: [{ name: '1-star ratings', data: byTag.slice(0, 10).map((row) => row.negativeRatings) }],
  };
  const repeatChart = {
    options: barOptions(repeatNegativeRaters.map((row) => row.bucket), '#b42318'),
    series: [{ name: 'Users', data: repeatNegativeRaters.map((row) => row.users) }],
  };

  return (
    <main className="kpi content-rating-quality">
      <header className="crq-header">
        <div>
          <p className="crq-eyebrow">Content quality dashboard</p>
          <h1>Content Rating Quality</h1>
          <p className="crq-subtitle">
            Weekly view by default. Highlights where 1-star content feedback is concentrated by content, language, tag, and teacher.
          </p>
        </div>
        <a className="crq-link-button" href={mixpanelBoardUrl} target="_blank" rel="noreferrer">
          Open Mixpanel
        </a>
      </header>

      <section className="crq-filters">
        <label>
          Start date
          <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </label>
        <label>
          End date
          <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </label>
        <label>
          Min 1-star ratings
          <input
            min="1"
            max="25"
            type="number"
            value={minNegativeRatings}
            onChange={(event) => setMinNegativeRatings(event.target.value)}
          />
        </label>
        <button type="button" onClick={loadDashboard} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </section>

      {error ? (
        <section className="crq-error">
          <h2>Dashboard could not load</h2>
          <p>{error.message || 'The content rating endpoint returned an error.'}</p>
          {error.detail ? <p>{error.detail}</p> : null}
          {error.setupRequired ? (
            <p>Deployment needs read-only prod Mongo configured as CONTENT_RATING_MONGO_HOST or MONGO_HOST_PROD.</p>
          ) : null}
        </section>
      ) : null}

      {dashboard ? (
        <>
          <section className="crq-cards">
            <Card label="Total 1/5-star ratings" value={formatNumber(summary.totalRatings)} helper="Only rows with rating 1 or 5" />
            <Card label="Negative ratings" value={formatNumber(summary.negativeRatings)} helper="1-star content feedback" />
            <Card label="Negative rate" value={formatPercent(summary.negativeRate)} helper="1-star / (1-star + 5-star)" />
            <UserCard
              label="Unique raters"
              value={formatNumber(summary.uniqueUsers)}
              helper="Distinct users in range"
              userEmails={summary.userEmails}
              userIds={summary.userIds}
            />
          </section>

          <section className="crq-grid">
            <section className="crq-panel crq-readout">
              <div className="crq-panel__head">
                <h2>Executive summary</h2>
                <span>Generated {formatDateTime(dashboard.generatedAt)}</span>
              </div>
              <ul>
                {(dashboard.automatedReadout || []).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="crq-method">
                <strong>How numbers are calculated:</strong> negative rate is 1-star ratings divided by all captured 1-star and 5-star ratings. Risk vs avg compares a segment's negative rate with the overall negative rate for the selected date range.
              </div>
            </section>

            <section className="crq-panel">
              <div className="crq-panel__head">
                <h2>Daily negative rate</h2>
              </div>
              <ReactApexChart
                options={trendOptions}
                series={[{ name: 'Negative rate', data: dailyTrend.map((row) => row.negativeRate) }]}
                type="line"
                height={280}
              />
            </section>
          </section>

          <section className="crq-panel">
            <div className="crq-panel__head">
              <h2>Watchlist</h2>
              <span>
                Showing {formatNumber(dashboard.watchlist?.length)} items with at least {formatNumber(minNegativeRatings)} one-star ratings
              </span>
            </div>
            <div className="crq-table-wrap">
              <table className="crq-table crq-table--watchlist">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Content</th>
                    <th>Language</th>
                    <th>Tag</th>
                    <th>Teacher</th>
                    <th>Program</th>
                    <th>1-star</th>
                    <th>5-star</th>
                    <th>Neg. rate</th>
                    <th>Risk vs avg</th>
                    <th>Last 1-star</th>
                    <th>Source</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboard.watchlist || []).map((row, index) => (
                    <tr key={`${row.class_id}-${row.content_locale}`}>
                      <td>{index + 1}</td>
                      <td>{shortText(row.class_name)}</td>
                      <td>{shortText(row.content_locale)}</td>
                      <td>{shortText(row.tag_name)}</td>
                      <td>{shortText(row.teacher_name)}</td>
                      <td>{shortText(row.program_name)}</td>
                      <td>{formatNumber(row.negativeRatings)}</td>
                      <td>{formatNumber(row.positiveRatings)}</td>
                      <td>{formatPercent(row.negativeRate)}</td>
                      <td>{Number(row.liftVsBaseline || 0).toFixed(2)}x</td>
                      <td>{formatDateTime(row.lastNegativeAt)}</td>
                      <td>
                        {row.recoveredSources?.length
                          ? row.recoveredSources.map((source) => `${formatSourceLabel(source.source)} (${source.negativeRatings})`).join(', ')
                          : 'Not available'}
                      </td>
                      <td>Review content, language, and placement</td>
                    </tr>
                  ))}
                  {!dashboard.watchlist?.length ? (
                    <tr>
                      <td colSpan="13">No content crossed the selected watchlist threshold.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          <section className="crq-chart-grid">
            <section className="crq-panel">
              <div className="crq-panel__head">
                <h2>By source</h2>
                {sourceRecovery.enabled ? (
                  <span>
                    {formatNumber(sourceRecovery.recoveredNegativeRatings)} with source, {formatNumber(sourceRecovery.unrecoveredNegativeRatings)} not captured
                  </span>
                ) : summary.missingSource === summary.totalRatings && summary.totalRatings > 0 ? (
                  <span>Source not available for this date range</span>
                ) : null}
              </div>
              <ReactApexChart options={sourceChart.options} series={sourceChart.series} type="bar" height={260} />
            </section>
            <section className="crq-panel">
              <div className="crq-panel__head">
                <h2>By language</h2>
              </div>
              <ReactApexChart options={localeChart.options} series={localeChart.series} type="bar" height={260} />
            </section>
            <section className="crq-panel">
              <div className="crq-panel__head">
                <h2>By tag</h2>
              </div>
              <ReactApexChart options={tagChart.options} series={tagChart.series} type="bar" height={260} />
            </section>
            <section className="crq-panel">
              <div className="crq-panel__head">
                <h2>Repeat negative raters</h2>
              </div>
              <ReactApexChart options={repeatChart.options} series={repeatChart.series} type="bar" height={260} />
            </section>
          </section>

          <section className="crq-table-grid">
            <SmallTable title="Highest-risk languages" rows={dashboard.byLocale} nameField="content_locale" />
            <SmallTable title="Highest-risk tags" rows={dashboard.byTag} nameField="tag_name" />
            <SmallTable title="Highest-risk teachers" rows={dashboard.byTeacher} nameField="teacher_name" />
            <SourceRecoveryTable rows={dashboard.bySource} recovery={sourceRecovery} />
          </section>

          <CoverageGapTables
            missingLanguageRows={dataCoverage.missingContentLanguage}
            missingSourceRows={dataCoverage.missingEntrySource}
          />

          <MethodologyNotes sourceRecovery={sourceRecovery} />
        </>
      ) : null}
    </main>
  );
}

export default ContentRatingQualityContainer;
